import React from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';
import { ExerciseProgress } from '../types';

interface ExerciseProgressChartProps {
  data: ExerciseProgress[];
  exerciseName: string;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const date = new Date(data.date);
    
    // Проверяем, что дата валидна
    if (isNaN(date.getTime())) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="text-sm font-medium text-gray-900 mb-1">
            {data.date}
          </p>
          <p className="text-sm text-gray-600">
            Вес: <span className="font-semibold">{data.weight} кг</span>
          </p>
          <p className="text-sm text-gray-600">
            Подходы: <span className="font-semibold">{data.sets}</span>
          </p>
          <p className="text-sm text-gray-600">
            Повторения: <span className="font-semibold">{data.reps}</span>
          </p>
        </div>
      );
    }
    
    return (
      <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
        <p className="text-sm font-medium text-gray-900 mb-1">
          {format(date, 'dd.MM.yyyy', { locale: ru })}
        </p>
        <p className="text-sm text-gray-600">
          Вес: <span className="font-semibold">{data.weight} кг</span>
        </p>
        <p className="text-sm text-gray-600">
          Подходы: <span className="font-semibold">{data.sets}</span>
        </p>
        <p className="text-sm text-gray-600">
          Повторения: <span className="font-semibold">{data.reps}</span>
        </p>
      </div>
    );
  }
  return null;
};

const formatWeight = (value: number) => `${value} кг`;

const ExerciseProgressChart: React.FC<ExerciseProgressChartProps> = ({ data, exerciseName }) => {
  console.log('ExerciseProgressChart - received data:', data);
  console.log('ExerciseProgressChart - exerciseName:', exerciseName);
  
  const chartData = data.map(item => {
    const date = new Date(item.date);
    return {
      ...item,
      date: isNaN(date.getTime()) ? item.date : format(date, 'dd.MM.yy', { locale: ru })
    };
  });
  
  console.log('ExerciseProgressChart - processed chartData:', chartData);

  if (data.length === 0) {
    console.log('ExerciseProgressChart - no data, showing empty state');
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <div className="text-gray-400 mb-3">
          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        </div>
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Нет данных о прогрессе
        </h3>
        <p className="text-xs text-gray-500">
          Для этого упражнения пока нет записей с весом.
        </p>
      </div>
    );
  }

  console.log('ExerciseProgressChart - rendering chart with data length:', chartData.length);
  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="mb-2">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Прогресс: {exerciseName}</h3>
      </div>
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 15, right: 30, left: 5, bottom: 10 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="date"
            angle={-45}
            textAnchor="end"
            height={40}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
          />
          <YAxis
            tickFormatter={formatWeight}
            tick={{ fontSize: 11 }}
            stroke="#6b7280"
            domain={['dataMin - 1', 'dataMax + 1']}
            width={35}
          />
          <Tooltip content={<CustomTooltip />} />
          <Line
            type="monotone"
            dataKey="weight"
            stroke="#3b82f6"
            strokeWidth={3}
            dot={{ fill: '#3b82f6', strokeWidth: 2, r: 4 }}
            activeDot={{ r: 6, stroke: '#ffffff', strokeWidth: 2 }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ExerciseProgressChart; 